import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {AppNotification} from "./notification-preview.config";
import {MeetingsService} from "../meetings-workflow/services/meetings.service";
import {CreateMeetingService} from "../meetings-workflow/services/create-meeting.service";
import {HandleErrorService} from "../../services/handle-error-service";
import {Subscription} from "rxjs";
import {NOTIFICATIONCONST} from "../../components/nav-bar/Models/notification.model";

@Component({
    selector: 'app-push-preview-card',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './preview-card.component.html',
    styleUrls: ['./preview-card.component.scss']
})
export class PushPreviewCardComponent implements OnInit, OnDestroy {
    @Input({ required: true }) notification!: AppNotification;
    @Input() autoClose = false;
    @Input() autoCloseTimeoutMs = 8000;

    @Output() dismiss = new EventEmitter<string>();
    @Output() clickNotification = new EventEmitter<AppNotification>();

    private timerId: any;
    private startTime: number | null = null;

    // Initialize with a safe default to prevent 0ms dismissals
    private remainingTime: number = 8000;
    private subscriptions = new Subscription();
    formattedTitle:string = '';

    constructor(
        private meetingsService: MeetingsService,
        private createMeetingService: CreateMeetingService,
        private handleErrorService: HandleErrorService,
    ) {}

    ngOnInit() {
        // Sync with the actual Input value on load
        this.remainingTime = this.autoCloseTimeoutMs;

        this.handleMeetingAnalysisNotification();

        // Initial Start
        if (this.autoClose) {
            this.startTimer();
        }

        this.formattedTitle = this.formatWithSoftHyphens(this.notification.title);

    }

    formatWithSoftHyphens(text: string): string {
        console.log("formatWithSoftHyphens ", text.split('').join('\u00AD'));
        return text.split('').join('\u00AD');
    }


    /**
     * Handles meeting analysis notification by fetching updated meeting data
     */
    private handleMeetingAnalysisNotification(): void {
        // Check if this is a meeting analysis notification
        if (this.notification?.type === NOTIFICATIONCONST.NotificationType.MEETING_ANALYSIS ||
            this.notification?.payload?.notificationType === NOTIFICATIONCONST.NotificationType.MEETING_ANALYSIS) {

            const meetingId = this.notification.payload?.meetingId;
            const accountId = this.notification.payload?.accountId;

            if (!meetingId || !accountId) {
                console.error('Missing meetingId or accountId in meeting analysis notification');
                return;
            }

            this.fetchAndUpdateMeetingData(Number(meetingId), String(accountId));
        }
    }

    /**
     * Fetches the latest meeting data and updates the expanded view
     */
    private fetchAndUpdateMeetingData(meetingId: number, accountId: string): void {
        const subscription = this.createMeetingService.getMeetingByMeetingId(meetingId, accountId)
            .subscribe({
                next: (response) => {
                    if (response.data) {
                        // Update the meeting in the MeetingsService's expandedViewMeetings$
                        this.meetingsService.updateExpandedViewMeeting(response.data);
                    }
                },
                error: (error) => {
                    console.error('Error fetching updated meeting after analysis:', error);
                    this.handleErrorService.handleError(error);
                }
            });

        this.subscriptions.add(subscription);
    }

    ngOnChanges(changes: SimpleChanges) {
        const change = changes['autoClose'];

        // FIX: Ignore the first change because ngOnInit handles initialization.
        // We only want to react to SUBSEQUENT changes (Expand/Collapse events).
        if (change && !change.isFirstChange()) {

            const isAutoCloseNow = change.currentValue;

            if (isAutoCloseNow) {
                // User collapsed the panel -> Resume timer
                this.startTimer();
            } else {
                // User expanded the panel -> Pause/Stop timer
                this.clearTimer();
                // Optional: Reset to full time or keep remaining?
                // Usually for "Expand" reading, we want to reset so it doesn't vanish instantly if they collapse again.
                this.remainingTime = this.autoCloseTimeoutMs;
            }
        }
    }

    ngOnDestroy() {
        this.clearTimer();
        this.subscriptions.unsubscribe();
    }

    // --- Timer Logic ---

    private startTimer() {
        if (this.timerId) return; // Already running

        this.startTime = Date.now();
        this.timerId = setTimeout(() => {
            this.dismiss.emit(this.notification.id);
        }, this.remainingTime);
    }

    private clearTimer() {
        if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
    }

    onMouseEnter() {
        if (!this.autoClose) return;

        this.clearTimer();

        if (this.startTime) {
            const elapsed = Date.now() - this.startTime;
            this.remainingTime -= elapsed;
        }
    }

    onMouseLeave() {
        // Only resume if autoClose is actually ON and we have time left
        if (this.autoClose && this.remainingTime > 0) {
            this.startTimer();
        }
    }

    onCardClick(event: MouseEvent) {
        this.clickNotification.emit(this.notification);
    }

    onCloseClick(event: MouseEvent) {
        event.stopPropagation();
        this.dismiss.emit(this.notification.id);
    }
}